import { useEffect, useRef, useState } from "react";
import { ALL_COUNTRIES, flagEmoji, countryName } from "@/lib/countries";
import { ChevronDown, Search } from "lucide-react";
import { overrideCountry } from "@/lib/api";

/**
 * Country picker — searchable dropdown of all countries with flags.
 * Compact trigger: 🇮🇳 India ▼.  Calls /api/geo/override with country_code on select.
 */
export default function CountryPicker({
  countryCode,
  onChange,
  placeholder = "Select Your Country",
  className = "",
  compact = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const filtered = query
    ? ALL_COUNTRIES.filter((c) =>
        c.name.toLowerCase().includes(query.toLowerCase()) ||
        c.code.toLowerCase() === query.toLowerCase(),
      ).slice(0, 80)
    : ALL_COUNTRIES.slice(0, 80);

  const pick = async (code) => {
    try {
      const pricing = await overrideCountry(code);
      if (onChange) onChange({ ...pricing, country_code: code });
    } catch { /* no-op */ }
    setOpen(false);
    setQuery("");
  };

  const triggerLabel = countryCode ? countryName(countryCode) : placeholder;
  const triggerFlag = countryCode ? flagEmoji(countryCode) : "🌍";

  return (
    <div ref={wrapRef} className={`relative inline-block ${className}`}>
      <button
        data-testid="country-picker-trigger"
        onClick={() => setOpen((v) => !v)}
        className={
          compact
            ? "inline-flex items-center gap-2 text-xs sm:text-sm tracking-wide text-[#A1A1AA] hover:text-[#F3F3F2] transition-colors font-body"
            : "inline-flex items-center gap-2.5 px-4 py-2.5 border border-[#27272A] bg-[#0E0E0E] hover:border-[#52525B] transition-colors text-sm font-body text-[#F3F3F2]"
        }
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="text-base sm:text-lg leading-none" aria-hidden="true">{triggerFlag}</span>
        <span className="truncate max-w-[180px]">{triggerLabel}</span>
        <ChevronDown className="w-3.5 h-3.5 opacity-70" />
      </button>

      {open && (
        <div
          data-testid="country-picker-menu"
          className="absolute z-40 mt-2 left-0 sm:left-auto sm:right-0 w-[300px] sm:w-[340px] max-h-[60vh] border border-[#27272A] bg-[#0E0E0E] shadow-2xl flex flex-col"
          role="listbox"
        >
          <div className="flex items-center gap-2 px-3 py-2.5 border-b border-[#27272A]">
            <Search className="w-3.5 h-3.5 text-[#52525B]" />
            <input
              data-testid="country-picker-search"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search countries"
              className="flex-1 bg-transparent outline-none text-sm text-[#F3F3F2] font-body placeholder:text-[#52525B]"
            />
          </div>
          <div className="overflow-y-auto flex-1 py-1">
            {filtered.length === 0 && (
              <div className="px-4 py-3 text-xs text-[#52525B] font-body">No matches</div>
            )}
            {filtered.map((c) => (
              <button
                key={c.code}
                data-testid={`country-option-${c.code}`}
                onClick={() => pick(c.code)}
                className={`w-full flex items-center gap-3 px-4 py-2 text-sm font-body hover:bg-[#141414] transition-colors text-left ${
                  countryCode === c.code ? "text-[#D4A373]" : "text-[#F3F3F2]"
                }`}
                role="option"
                aria-selected={countryCode === c.code}
              >
                <span className="text-base leading-none" aria-hidden="true">{flagEmoji(c.code)}</span>
                <span className="truncate">{c.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
